import { useParams, Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { CATEGORIES, PRODUCTS } from "@/data/products";
import ProductCard from "@/components/ProductCard";

const CategoryPage = () => {
  const { slug } = useParams();
  const category = CATEGORIES.find((c) => c.slug === slug);
  const products = PRODUCTS[slug] || [];

  if (!category) {
    return (
      <main className="max-w-7xl mx-auto px-6 py-20">
        <h1 className="font-serif-display text-3xl">Category not found</h1>
        <Link to="/" className="text-[#D4AF37] underline mt-4 inline-block">Go home</Link>
      </main>
    );
  }

  return (
    <main data-testid={`category-page-${slug}`}>
      {/* Hero */}
      <section className="relative">
        <div className="relative h-[46vh] min-h-[380px] md:h-[540px] w-full overflow-hidden">
          <img src={category.hero} alt={category.name} className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-7xl mx-auto px-6 md:px-12 w-full pb-14">
              <nav className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 mb-4">
                <Link to="/" className="hover:text-[#D4AF37]">Home</Link>
                <ChevronRight className="w-3 h-3" />
                <span className="text-[#D4AF37]">{category.short}</span>
              </nav>
              <h1 data-testid={`category-title-${slug}`} className="font-serif-display text-5xl lg:text-7xl text-white max-w-3xl leading-tight">
                {category.name}
              </h1>
              <p className="text-white/90 mt-4 max-w-2xl text-base lg:text-lg">{category.blurb}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Catalog</p>
            <h2 className="font-serif-display text-3xl lg:text-4xl">Browse {category.short.toLowerCase()}</h2>
          </div>
          <p className="text-sm text-[#4A4A4A]">{products.length} products · Click <span className="text-[#D4AF37]">Add to Quote</span> for pricing.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} categoryName={category.name} categorySlug={category.slug} />
          ))}
        </div>
      </section>

      {/* Other categories */}
      <section className="bg-white border-t border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Explore More</p>
          <h3 className="font-serif-display text-3xl mb-8">Other categories</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {CATEGORIES.filter((c) => c.slug !== slug).map((c) => (
              <Link key={c.slug} to={`/products/${c.slug}`} data-testid={`related-cat-${c.slug}`} className="group block border border-[#E5E5E5] hover:border-[#D4AF37]/60 bg-[#FAF9F6] overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={c.hero} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-4">
                  <p className="font-serif-display text-base">{c.name}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default CategoryPage;
